// Toda la plata que está afuera, en una sola lista.
//
// ── Por qué esta pantalla existe ──
//
// Los tres montones ya salían, cada uno en su sitio: lo prestado a las fincas
// en Fincas, el queso fiado en Clientes, los vales en Nómina. Cada pantalla
// contesta bien su pregunta.
//
// Pero la pregunta que uno se hace cuando SE SIENTA A COBRAR no es ninguna de
// esas tres: es «¿cuánta plata mía está en la calle y desde cuándo?». Y para
// contestarla había que entrar a tres pantallas y sumar de cabeza.
//
// ── Lo que NO se hace aquí ──
//
// No se recalcula nada. Las cuentas siguen viviendo donde ya estaban probadas
// —`creditosPendientes` para el fiado, el saldo de la finca del servidor— y
// aquí solo se juntan y se ordenan. Una cuarta pantalla que recalcule por su
// cuenta es una cuarta que puede decir algo distinto, y el día que dos no
// coincidan no se le cree a ninguna.

import { diasDesde } from './creditos';

// Las tres formas en que vuelve la plata. NO son lo mismo y mezclarlas en un
// solo total engaña: de $10.000.000 afuera, si $9.000.000 son préstamos a
// fincas, no va a entrar un peso a la caja — se van a descontar.
export const CLASES = {
  finca: {
    ico: '🚜', que: 'Préstamo a finca',
    vuelve: 'Se descuenta al pagarle la leche', efectivo: false,
  },
  cliente: {
    ico: '🧀', que: 'Queso fiado',
    vuelve: 'El cliente lo paga', efectivo: true,
  },
  trabajador: {
    ico: '👷', que: 'Vale de trabajador',
    vuelve: 'Se descuenta de la quincena', efectivo: false,
  },
};

const num = (x) => {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
};

// Los préstamos a fincas que no se han descontado.
export function dePrestamos(prestamos, fincas, hoy) {
  return (prestamos || [])
    .filter(p => p && !p.pago_id)
    .map(p => {
      const f = (fincas || []).find(x => x.id === p.finca_id);
      return {
        clase: 'finca', id: 'f' + p.id,
        quien: f ? f.nombre : '—',
        detalle: f && f.propietario ? f.propietario : null,
        monto: num(p.monto), fecha: p.fecha, nota: p.nota || null,
        dias: diasDesde(p.fecha, hoy),
        refId: p.finca_id,
      };
    });
}

// Los despachos de queso fiado. Vienen ya con los abonos repartidos.
export function deFiado(despachos, hoy) {
  return (despachos || []).map(d => ({
    clase: 'cliente', id: 'c' + d.id,
    quien: d.cliente || '—',
    detalle: d.ciudad ? '📍 ' + d.ciudad : null,
    monto: num(d.saldo), fecha: d.fecha,
    nota: d.parcial ? 'ya abonó parte' : null,
    dias: d.dias !== undefined ? d.dias : diasDesde(d.fecha, hoy),
    refId: d.cliente_id,
  }));
}

// Los vales sin descontar.
export function deVales(vales, trabajadores, hoy) {
  return (vales || [])
    .filter(v => v && !v.pago_id)
    .map(v => {
      const t = (trabajadores || []).find(x => x.id === v.trabajador_id);
      return {
        clase: 'trabajador', id: 'v' + v.id,
        quien: t ? t.nombre : '—',
        detalle: t && t.cargo ? t.cargo : null,
        monto: num(v.monto), fecha: v.fecha, nota: v.motivo || null,
        dias: diasDesde(v.fecha, hoy),
        refId: v.trabajador_id,
      };
    });
}

// Todo junto, del más viejo al más nuevo.
//
// Sin separar por clase: uno sale a cobrar por lo que más lleva afuera, sea de
// quien sea. Separado por tipo habría que leer tres listas y comparar fechas
// entre ellas, que es el trabajo que esta pantalla viene a quitar.
export function juntarTodo(partes, hoy) {
  const filas = [
    ...dePrestamos(partes.prestamos, partes.fincas, hoy),
    ...deFiado(partes.despachos, hoy),
    ...deVales(partes.vales, partes.trabajadores, hoy),
  ].filter(f => f.monto > 0);
  // Los que no tienen fecha entendible van al final: no se les puede dar una
  // antigüedad, y ponerlos de primeros los haría parecer los más urgentes.
  return filas.sort((a, b) => (b.dias ?? -1) - (a.dias ?? -1));
}

// El resumen que encabeza la pantalla.
export function resumen(filas) {
  const r = {
    total: 0, enEfectivo: 0, enDescuento: 0, cuantos: (filas || []).length,
    porClase: { finca: 0, cliente: 0, trabajador: 0 },
    masViejo: null,
  };
  for (const f of filas || []) {
    r.total += f.monto;
    r.porClase[f.clase] = (r.porClase[f.clase] || 0) + f.monto;
    // LA DISTINCIÓN QUE IMPORTA: solo el queso fiado entra como plata. Lo
    // demás vuelve como descuento, y un total único haría creer que hay diez
    // millones por entrar cuando nueve son rebajas de pagos futuros.
    if (CLASES[f.clase] && CLASES[f.clase].efectivo) r.enEfectivo += f.monto;
    else r.enDescuento += f.monto;
    if (f.dias !== null && (!r.masViejo || f.dias > r.masViejo.dias)) r.masViejo = f;
  }
  return r;
}
